import { FC, MouseEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import UnsavedChangesModal from '@/components/seop/UnsavedChangesModal';
import { SeopEditProvider, useSeopEdit } from '@/contexts/SeopEditContext';
import { seopService } from '@/services/seop.service';
import type { SeopMonth } from '@/types/seop';
import { EVENTO_MESES_ALTERADOS } from '@/utils/seopMeses';
import { seopMesComJanela } from '@/utils/seopFormat';
import { useSeopMonth } from '@/hooks/useSeopMonth';

const BASE = '/balanceamento-linha';

/**
 * As abas do módulo, na mesma arquitetura de informação do app original — é o que
 * faz o Herrison reconhecer a ferramenta dele aqui dentro.
 */
const TABS: { path: string; label: string }[] = [
  { path: BASE, label: 'Dashboard' },
  { path: `${BASE}/balanceamento`, label: 'Balanceamento' },
  { path: `${BASE}/demanda`, label: 'Demanda' },
  { path: `${BASE}/capacidade`, label: 'Capacidade' },
  { path: `${BASE}/cenarios`, label: 'Cenários' },
  { path: `${BASE}/cadastros`, label: 'Cadastros' },
];

interface SeopLayoutProps {
  children: ReactNode;
}

const SeopLayoutInner: FC<SeopLayoutProps> = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { month, setMonth } = useSeopMonth();
  const { dirty, save } = useSeopEdit();
  const [months, setMonths] = useState<SeopMonth[]>([]);
  const [scenarioName, setScenarioName] = useState('');
  // Para onde ir quando o usuário resolver o modal: rota ou troca de mês.
  const [pending, setPending] = useState<{ to: string } | { month: number } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadHeader = useCallback(async () => {
    try {
      const lista = await seopService.getMonths();
      // O índice de mês é POSICIONAL no motor — a ordem da lista é o contrato.
      setMonths([...lista].sort((a, b) => a.index - b.index));
    } catch {
      setMonths([]); // o seletor some; as telas mostram o próprio erro
    }
    try {
      setScenarioName(await seopService.getCurrentScenarioName());
    } catch {
      setScenarioName('');
    }
  }, []);

  useEffect(() => {
    loadHeader();
    // A seção "Meses do plano" (Cadastros) cria e corrige meses: o seletor precisa saber.
    window.addEventListener(EVENTO_MESES_ALTERADOS, loadHeader);
    return () => window.removeEventListener(EVENTO_MESES_ALTERADOS, loadHeader);
  }, [loadHeader]);

  // A query string carrega o mês entre as abas — sem isso, trocar de aba
  // remontaria o layout e voltaria para o mês 0.
  const querystring = `?mes=${month}`;

  const aplicar = useCallback(
    (destino: { to: string } | { month: number }) => {
      if ('to' in destino) navigate(destino.to);
      else setMonth(destino.month);
    },
    [navigate, setMonth],
  );

  /** Com edição pendente, segura a navegação e abre o modal. */
  const interceptar = useCallback(
    (destino: { to: string } | { month: number }, e?: MouseEvent) => {
      if (!dirty) {
        if ('month' in destino) setMonth(destino.month);
        return true;
      }
      e?.preventDefault();
      setPending(destino);
      return false;
    },
    [dirty, setMonth],
  );

  const sairSemSalvar = useCallback(() => {
    if (!pending) return;
    const destino = pending;
    setPending(null);
    aplicar(destino);
  }, [pending, aplicar]);

  const salvarESair = useCallback(async () => {
    if (!pending || !save) return;
    setIsSaving(true);
    try {
      await save();
      const destino = pending;
      setPending(null);
      aplicar(destino);
    } finally {
      setIsSaving(false);
    }
  }, [pending, save, aplicar]);

  return (
    <div className="flex flex-col">
      <div className="px-6 lg:px-10 pt-6 lg:pt-8">
        {/* Volta para o índice de relatórios. Passa pelo `interceptar` como as abas:
            sair daqui com edição pendente tem o mesmo custo em qualquer direção. */}
        <Link
          to="/relatorios"
          data-testid="seop-voltar-relatorios"
          onClick={(e) => interceptar({ to: '/relatorios' }, e)}
          className="mb-3 inline-flex items-center gap-1 text-xs text-content-muted hover:text-content-primary"
        >
          <span aria-hidden className="material-symbols-outlined text-[16px] leading-none">
            arrow_back
          </span>
          Relatórios
        </Link>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl lg:text-3xl font-bold text-content-primary">
                Balanceamento de Linha
              </h1>
              {scenarioName && (
                <span className="rounded-full border border-theme-border bg-surface-card px-3 py-0.5 text-xs text-content-secondary">
                  {scenarioName}
                </span>
              )}
            </div>
            <p className="text-content-muted text-sm">
              Demanda → roteiro → lote → tempo de processo → disponibilidade → capacidade → carga.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-content-muted">Período</span>
            <select
              aria-label="Período"
              value={month}
              onChange={(e) => interceptar({ month: Number(e.target.value) })}
              className="bg-surface-card border border-theme-border rounded-lg px-3 py-1.5 text-sm text-content-primary"
            >
              {months.length === 0 ? (
                <option value={month}>Mês {month + 1}</option>
              ) : (
                months.map((m) => (
                  <option key={m.id} value={m.index}>
                    {seopMesComJanela(m)}
                  </option>
                ))
              )}
            </select>
          </label>
        </div>

        <nav className="mt-6 flex gap-1 overflow-x-auto border-b border-theme-border">
          {TABS.map((tab) => {
            // `startsWith` só nas abas com caminho próprio: `BASE` é prefixo de TODAS,
            // e usá-lo acenderia o Dashboard em cima de qualquer outra aba.
            const active =
              pathname === tab.path || (tab.path !== BASE && pathname.startsWith(`${tab.path}/`));
            return (
              <Link
                key={tab.path}
                to={`${tab.path}${querystring}`}
                data-testid={`seop-tab-${tab.label.toLowerCase()}`}
                aria-current={active ? 'page' : undefined}
                onClick={(e) => interceptar({ to: `${tab.path}${querystring}` }, e)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
                  active
                    ? 'border-accent text-accent'
                    : 'border-transparent text-content-secondary hover:text-content-primary'
                }`}
              >
                {tab.label}
              </Link>
            );
          })}
        </nav>
      </div>
      {children}
      <UnsavedChangesModal
        open={pending !== null}
        isSaving={isSaving}
        onSaveAndLeave={save ? salvarESair : undefined}
        onLeave={sairSemSalvar}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};

/**
 * O provider fica AQUI, e não em cada `<Route>` do `App.tsx`: são seis rotas
 * repetindo o wrapper, e esquecer o provider numa delas quebraria o aviso de
 * edição pendente só naquela tela — em silêncio.
 */
const SeopLayout: FC<SeopLayoutProps> = ({ children }) => (
  <SeopEditProvider>
    <SeopLayoutInner>{children}</SeopLayoutInner>
  </SeopEditProvider>
);

export default SeopLayout;
